import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { RUN_META, ruleTip } from "@/lib/format";
import { RULE_CODES_IN_ORDER, ruleGroupName, ruleMeaning } from "@/lib/ruleMeanings";
import { enterIsOff } from "@/lib/decisionText";
import { DecisionPill } from "@/components/widgets";
import { api, downloadBlob, fmtErr } from "@/lib/api";
import { Badge, Button, Icon, Kbd, PageHead, Panel, RuleResult, Skeleton, dateTimeET, useViewport, etAndLocal, localOf } from "@/sentinel";

// Plain-language descriptions only. Anything that changes with configuration (which runs may ENTER,
// whether ENTER is armed, which data plan is active) is read from the API in <CurrentStatus />.
const RUNS = [
  { n: 1, plain: "Pre-market read. A first forecast and the full list of candidate trades. Nothing here is actionable." },
  { n: 2, plain: "Final look before the bell. Locks the pre-open view and shows what changed since Run 1." },
  { n: 3, plain: "The market opens. Captures the official opening price and compares it with the forecast." },
  { n: 4, plain: "15 minutes in. Reads which way the day is leaning and re-prices the trades." },
  { n: 5, plain: "30 minutes in. The last decision run of the morning." },
  { n: 6, plain: "Post-close review. Makes no trading decision: it grades every checkpoint against what actually happened." },
];

const DECISIONS = [
  ["NO TRADE", "The default and the most common answer. A gate is closed, and standing aside is the correct call."],
  ["WAIT", "Not a trade. Either one confirmation is still missing, or every rule passed but ENTER is switched off by the owner. The line under the decision says which. Hold and watch."],
  ["WAIT FOR OPEN", "Too early to judge. Wait for the market to actually open."],
  ["PROVISIONAL", "A first, non-binding read (Run 1 only)."],
  ["ENTER", "Every rule passed, on a run the owner allows, while the owner's ENTER switch is on. A human still has to confirm; Sentinel sends no order."],
  ["ENTER PRE-OPEN", "A rare, high-conviction call before the bell."],
];

const SCREENS = [
  ["Tonight", "The session desk: the 10:00 am research record first, then the old rule set's decision, its ticket or the nearest candidate that failed and why, and the ranked candidates underneath."],
  ["Checkpoints", "The frozen timeline: every run exactly as it was recorded, with what changed between runs."],
  ["Sources", "What data each run used, how fresh it was, and what was unavailable. Sentinel never fills a missing input with a guess."],
  ["Scoring", "The 10:00 am programme first: the quiet-day filter's forward record, kept against excluded sessions. Then the old rule set: this session's forecast accuracy, rolling accuracy, the Cost of saying no and the paper strategies."],
  ["History", "Every recorded live session at a glance, marked by decision."],
  ["Charts", "Price and forecast visuals for the selected session."],
  ["Backtest", "How the rules would have done on stored history, with exit tests, regimes and position sizing. Small samples are flagged, not headlined."],
  ["Old rule settings", "Every rule constant of the old rule set, in the versioned parameter register (analysts and owners). The 10:00 am programme does not use them."],
  ["Admin", "Safety controls, evidence review, data and scheduler status, alerts and users. Analysts can read it; changes are owner-only."],
  ["Phone card", "After Runs 2 to 5 a decision alert can go out by email or Telegram (the owner sets this in Admin → Alerts & AI). Its link opens a one-screen card with the decision and the reason."],
  ["Side experiments", "A separate workspace with its own rail, described under “Side experiments” below. It has no part in the SPX decision."],
  ["MegaCap playbook", "A separate workspace for the tests that start from the customer's SPX & Mega-Cap playbook (M1, M2, …): the test register and a map of where each playbook chapter sits. It has no part in the 10:00 am decision."],
  ["Project", "The owner's project workspace: board, decisions log, costs and handoff notes (owner only)."],
];

// The Side experiments workspace, screen by screen, in rail order.
const EQUITIES_SCREENS = [
  ["Fund discounts", "A month-end screen of the ten closed-end funds trading furthest below the value of what they hold, followed on paper month by month."],
  ["SPAC trust values", "Blank-cheque companies trading below the cash they hold in trust, read from their SEC filings, with a paper book under a fixed rule."],
  ["Tender offers", "Company buy-back offers filed with the SEC, read by an AI model for a small-holder (odd-lot) provision. Information only: read the filing itself before acting."],
  ["Track record", "The five-fund trend-following paper strategy with its buy-and-hold benchmark, the swing scanner's past plans and replays, and the news pilot."],
  ["Journal", "Your own log of trades you actually took, compared with what the model's plan would have done."],
  ["Watchlist", "The symbols you want to keep an eye on. Observation only."],
];

// Tests that run on paper. None of them can produce ENTER.
const PAPER_TESTS = [
  ["Paper strategies", "Fixed rules found in research (F3, C1 to C5, SF-A, SF-B) are evaluated at set times each day and settled after the close. They are on Scoring under Rolling evidence."],
  ["AI desk", `At ${["10:30", "13:00", "14:30"].map((t) => etAndLocal(t)).join(", ")} an AI model reads the same market data and picks no trade or one small spread, on paper. Each pick is tracked beside its mirror image so luck can be told from judgement.`],
  ["News pilot", "The desk collects company news to test, later and on paper only, whether an AI model reading it adds anything. It is on the Equities Track record screen."],
  ["Fund discounts and trend following", "Two results that held up on unseen history. Neither beat simply holding the S&P 500, so both are followed on paper for twelve months before anyone relies on them."],
];

// Keyboard shortcuts the shell implements (single keys are ignored while you are typing in a field).
const SHORTCUTS = [
  [["⌘", "K"], "Open the command palette (Ctrl K on Windows and Linux)"],
  [["G", "T"], "Go to Tonight"],
  [["G", "P"], "Go to the primary pick (Tonight desk)"],
  [["G", "C"], "Go to the candidates (Tonight desk)"],
  [["G", "K"], "Go to Checkpoints"],
  [["G", "S"], "Go to Sources"],
  [["G", "H"], "Go to Charts"],
  [["G", "E"], "Switch to Side experiments"],
  [["G", "M"], "Switch to the MegaCap playbook"],
  [["D"], "Change the session date"],
  [["["], "Collapse or expand the left rail"],
  [["⌘", "⇧", "L"], "Cycle theme: Dark → High contrast → Paper"],
  [["⌘", "⇧", "D"], "Toggle density: Comfortable ↔ Compact"],
  [["?"], "Open this guide"],
];

const prose = { margin: 0, fontSize: 14, lineHeight: "20px", color: "var(--text-secondary)" };
const listStyle = { margin: 0, paddingLeft: "var(--space-5)", fontSize: 13, lineHeight: "20px", color: "var(--text-secondary)", display: "flex", flexDirection: "column", gap: "var(--space-2)" };
const strong = { color: "var(--text-primary)", fontWeight: 600 };

/** A small sunken tile: a term and its explanation. */
function Tile({ term, children }) {
  return (
    <div className="sn-stack" style={{ gap: "var(--space-1)", padding: "var(--space-3)", background: "var(--surface-sunken)", borderRadius: "var(--radius-sm)" }}>
      <div style={{ fontSize: 13, lineHeight: "18px", fontWeight: 500, color: "var(--text-primary)" }}>{term}</div>
      <div className="sn-small sn-muted">{children}</div>
    </div>
  );
}

function TileGrid({ cols = 2, children }) {
  const { mobile } = useViewport();
  return <div style={{ display: "grid", gridTemplateColumns: mobile ? "minmax(0, 1fr)" : `repeat(${cols}, minmax(0, 1fr))`, gap: "var(--space-2)" }}>{children}</div>;
}

const runList = (runs) => (runs && runs.length ? runs.map((r) => `R${r} (${RUN_META[r]?.time ? etAndLocal(RUN_META[r].time) : "—"})`).join(", ") : "none");
const money = (v) => (v == null || Number.isNaN(Number(v)) ? "—" : `${Number(v) < 0 ? "−" : ""}$${Math.abs(Number(v)).toFixed(2)}`);

/** The ENTER switch in one line, for every role. `enter` is health.enter from GET /health. */
function enterStateText(enter) {
  const off = enterIsOff(enter);
  if (off === undefined) return null;
  if (!off) return `ENTER is on and can print on ${runList(enter.runs)}.`;
  if (enter.enabled === false) return "ENTER is switched off by the owner (edge not proven). Even a setup that passes every rule shows as WAIT or NO TRADE.";
  return `${enter.reason || "ENTER is switched on but not in effect."} Even a setup that passes every rule shows as WAIT or NO TRADE.`;
}

/** Live facts, read from the API. Nothing here is hard-coded. */
function CurrentStatus() {
  const { can, health } = useAuth();
  const analyst = can("analyst");
  const { data: enter, isLoading, isError } = useQuery({ queryKey: ["enter-status"], queryFn: api.enterStatus, enabled: analyst, retry: false });
  const plan = health?.data_plan;
  const edge = enter?.edge_check;
  // Every role reads the ENTER state from /health; analysts also see the edge check below it.
  const viewerEnter = health?.enter;
  const viewerOff = enterIsOff(viewerEnter);
  const viewerText = enterStateText(viewerEnter);
  return (
    <Panel title="Current status" icon="Activity" data-testid="guide-status"
      meta={<span className="sn-small sn-faint">Read live from the server</span>}>
      <div className="sn-stack">
        <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
          <span className="sn-micro" style={{ minWidth: 120 }}>ENTER switch</span>
          {viewerText ? (
            <span className="sn-row" style={{ gap: "var(--space-2)", alignItems: "flex-start" }} data-testid="guide-enter-line">
              <Badge icon={viewerOff ? "CircleMinus" : "CircleCheck"} data-testid="guide-enter-state">{viewerOff ? "OFF" : "ON"}</Badge>
              <span className="sn-small sn-muted" style={{ maxWidth: "70ch" }}>{viewerText}</span>
            </span>
          ) : health ? (
            <span className="sn-small sn-muted">The server did not report the ENTER state. Analysts and owners can read it in Admin → Safety.</span>
          ) : (
            <span className="sn-small sn-muted">Status unavailable right now.</span>
          )}
        </div>
        {analyst && (
          <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
            <span className="sn-micro" style={{ minWidth: 120 }}>Edge check</span>
            {isLoading ? (
              <Skeleton width={240} />
            ) : isError || !enter ? (
              <span className="sn-small sn-muted">Status unavailable right now.</span>
            ) : (
            <span className="sn-stack" style={{ gap: "var(--space-1)" }}>
              <span className="sn-small sn-muted" data-testid="guide-enter-detail">
                {enter.enabled ? `The owner has switched ENTER on for ${runList(enter.runs_whitelist)}.` : "The owner has not switched ENTER on."}
                {enter.enabled && enter.enabled_at ? ` Switched on ${dateTimeET(enter.enabled_at)}.` : ""}
              </span>
              {edge && (
                <span className="sn-row sn-small sn-muted" style={{ gap: "var(--space-2)" }} data-testid="guide-edge-check">
                  <RuleResult result={edge.ok ? "PASS" : "UNCONFIRMED"}>{edge.ok ? "EDGE SHOWN" : "EDGE NOT PROVEN"}</RuleResult>
                  n = <span className="sn-num">{edge.n ?? "—"}</span> · expectancy <span className="sn-num">{money(edge.expectancy)}</span> · lower 95% CI <span className="sn-num">{money(edge.lower_ci95)}</span>
                  {edge.reason ? ` · ${edge.reason}` : ""}
                </span>
              )}
            </span>
            )}
          </div>
        )}
        <div className="sn-row" style={{ gap: "var(--space-3)", alignItems: "flex-start" }}>
          <span className="sn-micro" style={{ minWidth: 120 }}>Market data</span>
          <span className="sn-small sn-muted" data-testid="guide-data-plan">
            {plan
              ? <>Options {String(plan.options || "—")} · indices {String(plan.indices || "—")} · {plan.realtime ? "real-time" : "not real-time"} (Massive.com).</>
              : health ? "Live sessions read the Massive.com (Polygon) feed. Sources shows the provenance of every input." : "Status unavailable right now."}
          </span>
        </div>
      </div>
    </Panel>
  );
}

/** The rule codes the desk prints, each with the same plain sentence the desk uses. */
function RuleCodes() {
  const { mobile } = useViewport();
  return (
    <Panel title="Codes you will see" icon="ListChecks" data-testid="guide-rule-codes"
      meta={<span className="sn-small sn-faint">The same words the desk and the phone card use</span>}>
      <div className="sn-stack" style={{ gap: "var(--space-3)" }}>
        <p style={prose}>
          When no trade qualifies, the desk names the rule that stopped the nearest candidate. The letter says what kind of check it is;
          the number is the rule within that group. Hover a row for the exact limit (analysts can read every limit on Thresholds).
        </p>
        <div style={{ overflowX: "auto" }}>
          <table className="sn-table" aria-label="Rule codes and their meanings" data-testid="guide-rule-codes-table">
            <thead>
              <tr><th scope="col">Code</th>{!mobile && <th scope="col">Checks</th>}<th scope="col">What it means when it blocks</th></tr>
            </thead>
            <tbody>
              {RULE_CODES_IN_ORDER.map((code) => (
                <tr key={code} title={ruleTip[code] || undefined} data-testid={`guide-rule-${code}`}>
                  <td className="sn-num" style={{ whiteSpace: "nowrap", fontWeight: 600, color: "var(--text-primary)" }}>{code}</td>
                  {!mobile && <td className="sn-small sn-muted" style={{ whiteSpace: "nowrap" }}>{ruleGroupName(code)}</td>}
                  <td className="sn-small" style={{ color: "var(--text-secondary)" }}>{ruleMeaning(code)}{mobile ? <span className="sn-faint"> · {ruleGroupName(code)}</span> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Panel>
  );
}

function GettingAround() {
  return (
    <Panel title="Getting around" icon="Map" data-testid="guide-navigation">
      <div className="sn-stack">
        <p style={prose}>Four surfaces each do one job, and they are the same on every screen.</p>
        <TileGrid>
          <Tile term="Left rail: where you are and where you can go">
            The SPX desk is grouped into Session, Evidence, Research, Configure and Help. Side experiments, the MegaCap playbook and the Project workspace
            are separate workspaces under Other desks, each with its own rail and accent colour. Screens your role cannot open show a lock and name the role they need.
            Your account, theme and density live in the menu at the foot of the rail. On a phone the rail becomes a bottom tab bar with a More sheet.
          </Tile>
          <Tile term="Status bar: the state of the selected session">
            Across the top of every SPX screen: the session date (select it to change sessions), Run N / 6 with the six-square checkpoint indicator,
            the countdown to the next run, the data provenance badge, the kill-switch state, the ET and Sydney clocks, and the alerts bell.
          </Tile>
          <Tile term="Command palette: get anywhere by name">
            Press <Kbd>⌘</Kbd> <Kbd>K</Kbd> (<Kbd>Ctrl</Kbd> <Kbd>K</Kbd> on Windows and Linux) or use the search field in the rail.
            Type a screen name or an action such as switching theme, toggling density, changing the session date or printing.
          </Tile>
          <Tile term="Legal strip: always visible">
            The line at the very top: not financial advice, not an offer, and no orders are placed by this system. It never scrolls away.
          </Tile>
        </TileGrid>
        <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
          <span className="sn-micro">Keyboard shortcuts</span>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "var(--space-1) var(--space-6)" }} data-testid="guide-shortcuts">
            {SHORTCUTS.map(([keys, what]) => (
              <div key={what} className="sn-row" style={{ gap: "var(--space-3)", flexWrap: "nowrap", padding: "var(--space-1) 0", borderBottom: "1px solid var(--border-hairline)" }}>
                <span className="sn-row" style={{ gap: 4, flexWrap: "nowrap", minWidth: 76 }}>
                  {keys.map((k, i) => (
                    <span key={i} className="sn-row" style={{ gap: 4, flexWrap: "nowrap" }}>
                      {i > 0 && keys[0] === "G" && <span className="sn-small sn-faint">then</span>}
                      <Kbd>{k}</Kbd>
                    </span>
                  ))}
                </span>
                <span className="sn-small sn-muted">{what}</span>
              </div>
            ))}
          </div>
          <p className="sn-small sn-faint" style={{ margin: 0 }}>
            Single-key shortcuts are ignored while you are typing in a field. There is deliberately no shortcut for confirming ENTER or for the kill switch.
          </p>
        </div>
      </div>
    </Panel>
  );
}

export default function Guide() {
  const { mobile } = useViewport();
  const [dl, setDl] = useState(false);
  const downloadGuide = async () => {
    setDl(true);
    try {
      const blob = await api.exportGuidePdf();
      downloadBlob(blob, "SPX_Sentinel_Beginner_Guide.pdf");
      toast.success("Beginner guide (PDF) downloaded.");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || e.message || "Download failed");
    } finally {
      setDl(false);
    }
  };
  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="guide-page">
      <PageHead
        crumb={["Help", "Guide & tour"]}
        title="Guide"
        context={<>Press <Kbd>?</Kbd> from any screen to come back here.</>}
        actions={
          <Button icon="Download" loading={dl} onClick={downloadGuide} disabled={dl} data-testid="guide-pdf-btn"
            title="Download the plain-language beginner guide as a shareable PDF">
            {dl ? "Preparing…" : "Download guide (PDF)"}
          </Button>
        }
      />

      <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="guide-newcomer">
        <section className="sn-panel" style={{ padding: "var(--space-6)" }}>
          <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
            <span className="sn-row sn-micro" style={{ gap: "var(--space-1)" }}><Icon name="BookOpen" size={14} />Start here</span>
            <h2 style={{ margin: 0, fontSize: 24, lineHeight: "32px", fontWeight: 600, color: "var(--text-primary)" }}>
              A research desk for same-day S&amp;P 500 credit spreads, built around one moment: 10:00 am New York time ({localOf("10:00")} where you are).
            </h2>
            <p style={{ ...prose, maxWidth: "74ch" }}>
              The question it is answering is whether, using only what is known at 10:00, it can tell when the index is likely to stay
              quiet, or lean one way, and whether a bull put or bear call spread then pays after real prices and costs. Until the evidence
              says so, nothing here is a signal: it never places an order, connects to no broker, and never makes up data. You always make the final call.
            </p>
          </div>
        </section>

        <Panel title="The 10:00 am programme" icon="Timer" data-testid="guide-ten-am">
          <div className="sn-stack">
            <p style={prose}>
              Each trading day at {etAndLocal("10:01:15")}, once the 10:00 minute has closed, the desk records the index level and what was
              known: the one-day volatility index VIX1D, the move from the open, the overnight gap and the releases still due that day.
              It notes whether the <b style={strong}>quiet-day filter</b> keeps the session: the filter was chosen on 2021 to 2023, frozen,
              and passed its one test on 2024, roughly halving the share of sessions that moved 1% after 10:00. After the close the record
              adds what happened. The 10:00 record leads the Today desk and the Scoring page, and appears on the phone card.
            </p>
            <p style={prose}>
              The filter forecasts movement, not profit. Options are cheaper on calm-looking mornings for the same reason, so the spread
              test on real 10:00 prices decides whether a trade pays. Until then the record is research only.
            </p>
          </div>
        </Panel>

        <CurrentStatus />

        <GettingAround />

        <Panel title="First, the words" icon="BookOpen" data-testid="guide-glossary">
          <TileGrid>
            {[
              ["SPX / SPXW", "Options on the S&P 500 stock index."],
              ["0DTE", "“Zero days to expiry”: options that expire the same day they are traded."],
              ["Vertical spread", "Buying one option and selling another so both your profit and loss are capped."],
              ["Credit vs debit", "You either collect cash up front (credit) or pay it (debit) to open the trade."],
              ["Strike / breakeven", "The price levels that decide whether the trade makes or loses money."],
              ["Max profit / max loss", "The most you can make or lose, fixed and known before you enter."],
            ].map(([t, d]) => <Tile key={t} term={t}>{d}</Tile>)}
          </TileGrid>
        </Panel>

        <div className="sn-grid-12" style={mobile ? { gridTemplateColumns: "minmax(0, 1fr)" } : null}>
          <Panel title="The six decisions" icon="Shield" data-testid="guide-decisions" className="sn-col-6">
            <div className="sn-stack">
              {DECISIONS.map(([label, desc]) => (
                <div key={label} style={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr)", gap: "var(--space-3)", alignItems: "start" }}>
                  <span><DecisionPill label={label} size="sm" testId={`guide-pill-${label}`} /></span>
                  <div className="sn-small sn-muted" style={{ marginTop: 2 }}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="The old rule set: six checkpoints" icon="GitCommitVertical" data-testid="guide-runs" className="sn-col-6">
            <div className="sn-stack" style={{ gap: "var(--space-2)" }}>
              {RUNS.map((r) => (
                <div key={r.n} className="sn-row" style={{ gap: "var(--space-3)", flexWrap: "nowrap", alignItems: "flex-start", padding: "var(--space-2) var(--space-3)", background: "var(--surface-sunken)", borderRadius: "var(--radius-sm)" }}>
                  <div className="sn-num" style={{ flex: "none", width: 72, fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                    R{r.n}<br /><span className="sn-faint" style={{ fontSize: 12, fontWeight: 400 }}>{etAndLocal(RUN_META[r.n].time)}</span>
                  </div>
                  <div className="sn-small sn-muted">{r.plain}</div>
                </div>
              ))}
              <p className="sn-small sn-faint" style={{ margin: 0 }}>
                These are the rules the desk was first built on. No test found an edge after costs, so they are kept for comparison
                below the 10:00 record and ENTER stays off. On an early-close day the post-close review follows the actual close.
              </p>
            </div>
          </Panel>
        </div>

        <RuleCodes />

        <Panel title="What each screen is for" icon="LayoutDashboard" data-testid="guide-screens">
          <TileGrid>
            {SCREENS.map(([name, desc]) => <Tile key={name} term={name}>{desc}</Tile>)}
          </TileGrid>
        </Panel>

        <Panel title="Try it in 60 seconds" icon="ListChecks" data-testid="guide-try">
          <ol style={listStyle}>
            <li>On <b style={strong}>Tonight</b> (<Kbd>G</Kbd> then <Kbd>T</Kbd>), read the current decision. When the scheduler is armed in Admin, the checkpoints run by themselves at their ET times.</li>
            <li>Watch the checkpoints fill in on the status bar's Run N / 6 indicator. The decision usually lands on <b style={strong}>NO TRADE</b>. ENTER needs every rule to pass and the owner's ENTER switch to be on; while the switch is off, a setup that passes every rule shows as WAIT.</li>
            <li>Under the decision, expand a candidate to see its full rule audit and the profit/loss if the market moves.</li>
            <li>Open <b style={strong}>History</b> from the Evidence group to see past days at a glance. <Kbd>⌘</Kbd> <Kbd>K</Kbd> finds any screen by name.</li>
          </ol>
        </Panel>

        <Panel title="Reading the evidence honestly" icon="SlidersHorizontal" data-testid="guide-newcomer-edge">
          <div className="sn-stack">
            <p style={prose}>The <b style={strong}>Backtest</b> screen helps you test an edge before trusting it:</p>
            <TileGrid>
              {[
                ["Sample size first", "No result is headlined until it rests on at least 30 trades. Below that, the table is shown with its n and marked as a small sample."],
                ["Best way to exit", "Tests taking profit early, cutting losses, or closing by a set time, against simply holding to the close."],
                ["Best time to trade", "Slices the history by day of week, how calm the market was and the opening gap."],
                ["A safety net", "Set a daily loss limit in Admin; if a live day breaches it, the desk flips to NO TRADE."],
              ].map(([t, d]) => <Tile key={t} term={t}>{d}</Tile>)}
            </TileGrid>
          </div>
        </Panel>

        <Panel title="Paper tests: tracked, never traded" icon="FlaskConical" data-testid="guide-paper-tests">
          <div className="sn-stack">
            <p style={prose}>
              No rule tested so far has shown an edge after real trading costs, which is why ENTER is off. Several ideas are now being
              followed forward on paper. Each has a sample size and a date fixed in advance, and is judged once when it gets there, not before.
              A good-looking month in any of these panels is not a reason to trade.
            </p>
            <TileGrid>
              {PAPER_TESTS.map(([t, d]) => <Tile key={t} term={t}>{d}</Tile>)}
            </TileGrid>
          </div>
        </Panel>

        <Panel title="Side experiments" icon="ScanSearch" data-testid="guide-equities">
          <div className="sn-stack">
            <p style={prose}>
              Open it from Other desks in the rail, or press <Kbd>G</Kbd> then <Kbd>E</Kbd>. These are paper records of ideas outside the main 10:00 am
              programme, kept running at a modest pace and each judged once on its own date. The swing-setup scanner that used to open this desk is
              hidden: every replay of it found <b style={strong}>no edge</b>.
            </p>
            <TileGrid>
              {EQUITIES_SCREENS.map(([name, desc]) => <Tile key={name} term={name}>{desc}</Tile>)}
            </TileGrid>
          </div>
        </Panel>

        <Panel title="Good to know" icon="Info" data-testid="guide-newcomer-limits">
          <ul style={listStyle}>
            <li>When the desk shows a pick, its exit plan is frozen with it. The default plan is to hold to settlement at the close; the ticket says so and gives the time until which the entry is valid.</li>
            <li>The Cost of saying no on Scoring is a tally of what the blocked trades would have done, with its range of uncertainty. It is not a verdict on whether to switch ENTER on.</li>
            <li>The probabilities come from a deliberately simple model. It is built to be cautious, not clever, and Backtest shows how well it is calibrated.</li>
            <li>A run that fires late, or a manual run outside its window, is tagged LATE and does not count toward ENTER or the scoring rollups.</li>
            <li>Nothing here is financial advice, and no trade is ever placed for you. Sentinel ranks and remembers; you decide.</li>
          </ul>
        </Panel>
      </div>
    </div>
  );
}
